import { captureException } from '@sentry/nextjs'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'

export interface InfiniteLoadByOffsetProps<T> {
  id: string
  getItems: Function
  page_size?: number
  onNextItems?: Function
  refetchMarker?: string
  enrichedItemSorter?: (a: T, b: T) => number
  comparer?: Function
  rawToEnrichedComparer?: Function
}

export interface InfiniteLoadByOffsetReturn<T, RawT> {
  rawData: RawT[]
  data: T[]
  error: any
  setData: Dispatch<SetStateAction<T[]>>
  fetchNext: () => void
  hasNext: boolean
  refetch: () => void
  isLoading: boolean
  nextIsLoading: boolean
  isRefetching: boolean
  onSubscriptionDataAdd?: Function
  onSubscriptionDataRemove?: Function
}

export function useInfiniteLoadByOffset<T, RawT>({
  id,
  getItems,
  page_size,
  onNextItems,
  refetchMarker,
  enrichedItemSorter,
  comparer,
  rawToEnrichedComparer,
}: InfiniteLoadByOffsetProps<T>): InfiniteLoadByOffsetReturn<T, RawT> {
  const [data, setData] = useState<T[]>([])
  const [rawData, setRawData] = useState<RawT[]>([])
  const [error, setError] = useState<any | undefined>()

  const [page, setPage] = useState<number>(0)
  const [hasNext, setHasNext] = useState(true)

  const [isLoading, setIsLoading] = useState(true)
  const [nextIsLoading, setNextIsLoading] = useState(false)
  const [isRefetching, setIsRefetching] = useState(false)

  const fetchInitialData = useCallback(async (): Promise<void> => {
    setIsLoading(true)

    try {
      const { rawData, data, hasMore } = await getItems({ offset: 0, limit: page_size })
      setHasNext(hasMore)
      onNextItems && onNextItems(data)
      setPage(1)
      setRawData(rawData)
      setData(data)
      setError(undefined)
    } catch (err: any) {
      setError(err)
      console.log("Couldn't fetch data", err)
      captureException(err)
    } finally {
      setIsLoading(false)
    }
  }, [getItems, onNextItems, page_size])

  useEffect(() => {
    fetchInitialData()
  }, [refetchMarker])

  const fetchNext = useCallback(async (): Promise<void> => {
    if (hasNext && !nextIsLoading && page_size) {
      setNextIsLoading(true)
      const { data, hasMore, rawData } = await getItems({
        offset: page * page_size,
        limit: page_size,
      })
      onNextItems && onNextItems(data)
      setData((prev: T[]) => [...prev, ...data])
      setRawData((prev: RawT[]) => [...prev, ...rawData])
      setPage((prev) => prev + 1)
      setHasNext(hasMore)
      setNextIsLoading(false)
    }
  }, [getItems, hasNext, nextIsLoading, onNextItems, page, page_size])

  const refetch = useCallback(async (): Promise<void> => {
    setIsRefetching(true)
    await fetchInitialData()
    setIsRefetching(false)
  }, [fetchInitialData])

  useEffect(() => {
    refetch()
  }, [id])

  // Handler for new items from subscription
  const handleSubscriptionDataAdd = useCallback(
    (enrichedNewItems: T[], newItems: RawT[]) => {
      // page === 0 means the first page is not loaded yet, thus, skip the suscription handler
      if (!page || !page_size || !enrichedItemSorter) {
        return
      }

      const newRawData = [...newItems, ...rawData]
      setRawData(newRawData)
      const newData = [...enrichedNewItems, ...data].sort(enrichedItemSorter)
      if (page * page_size >= newData.length) {
        setData(newData)
      } else {
        setData(newData.slice(0, page * page_size))
        setHasNext(true)
      }
    },
    [data, enrichedItemSorter, page, page_size, rawData]
  )

  // Handler for removed items from subscription
  const handleSubscriptionDataRemove = useCallback(
    (itemsToRemove: RawT[]) => {
      // page === 0 means the first page is not loaded yet, thus, skip the suscription handler
      if (!page || !page_size || !comparer || !rawToEnrichedComparer) {
        return
      }

      const newRawData = rawData.filter(
        (item: RawT) => !itemsToRemove.find((itemToRemove) => comparer(item, itemToRemove))
      )
      setRawData(newRawData)

      const newData = data.filter(
        (item: T) =>
          !itemsToRemove.find((itemToRemove: RawT) => rawToEnrichedComparer(itemToRemove, item))
      )
      const replenishingOffset = newData.length
      const replenishingLimit = data.length - newData.length
      setData(newData)

      // this is the number of items that will be replenished after removing items
      if (!replenishingLimit) {
        return
      }

      ;async () => {
        setNextIsLoading(true)
        const {
          data: replenishingData,
          hasMore,
          rawData: replenishingRawData,
        } = await getItems({
          offset: replenishingOffset,
          limit: replenishingLimit,
        })

        onNextItems && onNextItems(replenishingData)
        setData((prev: T[]) => [...prev, ...replenishingData])
        setRawData((prev: RawT[]) => [...prev, ...replenishingRawData])
        setHasNext(hasMore)
        setNextIsLoading(false)
      }
    },
    [comparer, data, getItems, onNextItems, page, page_size, rawData, rawToEnrichedComparer]
  )

  return {
    rawData,
    data,
    error,
    setData,
    fetchNext,
    hasNext,
    refetch,
    isLoading,
    nextIsLoading,
    isRefetching,
    onSubscriptionDataAdd: handleSubscriptionDataAdd,
    onSubscriptionDataRemove: handleSubscriptionDataRemove,
  }
}
